import TerminalAPI from '@/features/api/terminal/terminal'
import { defaultSoundPreferences } from '@/features/notifications/notification-service-extension-shared'
import { saveSoundPrefsToAppGroup } from '@/features/notifications/utils/soundHelpers'
import { NotificationPreferences, NotificationSettings } from '@/features/settings/types'
import {
    mapPreferencesToSettings,
    mapSettingsToNotificationPreferences,
    mapSettingsToSoundPreferences,
} from '@/features/settings/utils/notificationSettingsTransforms'
import { logger } from '@/logger'
import storage from '@/services/storage'
import { createAsyncThunk } from '@reduxjs/toolkit'

type NotificationPreferencesThunkApiConfig = { rejectValue: string }

const createThunk = createAsyncThunk.withTypes<NotificationPreferencesThunkApiConfig>()

export const loadNotificationPreferences = createThunk<NotificationSettings, string>(
    'settings/loadNotificationPreferences',
    async (consumerUuid, { rejectWithValue }) => {
        try {
            const notificationPrefs = await TerminalAPI.fetchNotificationPreferences(consumerUuid)

            const soundPrefs =
                (await storage.getNotificationSoundPreferences()) ?? defaultSoundPreferences

            const prefs = mapPreferencesToSettings(notificationPrefs, soundPrefs)

            return prefs
        } catch (e) {
            logger.errorOnly(e)

            return rejectWithValue('Failed to load notification preferences.')
        }
    }
)

export const updateNotificationPreferences = createThunk<
    NotificationSettings,
    { consumerUuid: string; prefs: NotificationSettings }
>(
    'settings/updateNotificationPreferences',
    async ({ consumerUuid, prefs }, { rejectWithValue }) => {
        try {
            const soundPrefs = mapSettingsToSoundPreferences(prefs)

            await storage.setNotificationSoundPreferences(soundPrefs)
            await saveSoundPrefsToAppGroup(soundPrefs)

            const requestBody = mapSettingsToNotificationPreferences(prefs)
            const notificationPrefs: NotificationPreferences =
                await TerminalAPI.updateNotificationPreferences(consumerUuid, requestBody)

            const newPrefs = mapPreferencesToSettings(notificationPrefs, soundPrefs)

            return newPrefs
        } catch (e) {
            logger.errorOnly(e)
            return rejectWithValue('Failed to update notification preferences.')
        }
    }
)
